import { useState, useEffect } from 'react';
import { api } from '@/App';

/**
 * Custom hook for fetching retained earnings from previous open periods
 * 
 * @param {Object} periodData - Current period data
 * @returns {Object} Previous period retained earnings data
 */
export const usePreviousPeriodRetainedEarnings = (periodData) => {
  const [previousPeriodRetainedEarnings, setPreviousPeriodRetainedEarnings] = useState(null);

  useEffect(() => {
    const fetchPreviousPeriodRetainedEarnings = async () => {
      if (!previousPeriodRetainedEarnings || !previousPeriodRetainedEarnings.loading) {
        return;
      }

      try {
        const allPeriodsRes = await api.get('/periods');
        const allPeriods = allPeriodsRes.data.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
        const previousPeriod = allPeriods.find(p => p.name === previousPeriodRetainedEarnings.periodName);
        
        if (!previousPeriod) {
          setPreviousPeriodRetainedEarnings(null);
          return;
        }

        const params = { period_id: previousPeriod.id };
        const [purchasesRes, salesRes, expensesRes, centralExpensesRes, managersRes, branchesRes] = await Promise.all([
          api.get('/purchases', { params }),
          api.get('/sales', { params }),
          api.get('/expenses', { params }),
          api.get('/central-expenses', { params }),
          api.get('/managers'),
          api.get('/branches')
        ]);

        const purchases = purchasesRes.data;
        const sales = salesRes.data;
        const expenses = expensesRes.data;
        const centralExpenses = centralExpensesRes.data;
        const managersData = managersRes.data;
        const branchesData = branchesRes.data;

        // Calculate branch dividends (simplified version)
        const branchProfits = {};
        branchesData.forEach(branch => {
          const branchPurchases = purchases.filter(p => p.branch_id === branch.id);
          const branchSales = sales.filter(s => s.branch_id === branch.id);
          const branchExpenses = expenses.filter(e => e.branch_id === branch.id);

          const revenue = branchSales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
          const cost = branchPurchases.reduce((sum, p) => sum + (p.total_amount || 0), 0);
          const expense = branchExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
          
          branchProfits[branch.id] = {
            profit: revenue - cost - expense,
            shareholders: []
          };

          managersData.forEach(manager => {
            const share = manager.branch_shares?.find(s => s.branch_id === branch.id);
            if (share) {
              branchProfits[branch.id].shareholders.push({
                manager_id: manager.id,
                manager_name: manager.name,
                percentage: share.percentage,
                position: manager.position
              });
            }
          });
        });

        // Calculate PUSAT profit
        const centralRevenue = centralExpenses
          .filter(e => e.amount < 0)
          .reduce((sum, e) => sum + Math.abs(e.amount), 0);
        const centralActualExpenses = centralExpenses
          .filter(e => e.amount > 0)
          .reduce((sum, e) => sum + e.amount, 0);
        const pusatProfit = centralRevenue - centralActualExpenses;

        // Calculate dividends
        let totalMitraDividend = 0;
        let totalManagerDividend = 0;
        let totalInvestorDividend = pusatProfit;

        Object.values(branchProfits).forEach(branchData => {
          const profitLoss = branchData.profit;
          const mitraOnly = branchData.shareholders.filter(sh => sh.position === 'Mitra');
          const managersOnly = branchData.shareholders.filter(sh => sh.position === 'Manager');

          // Mitra dividend
          const mitraPercentage = mitraOnly.reduce((sum, m) => sum + (m.percentage || 0), 0);
          const mitraDividend = Math.round((mitraPercentage / 100) * profitLoss);
          totalMitraDividend += mitraDividend;

          // Profit after mitra = sisa laba rugi cabang
          const profitAfterMitra = profitLoss - mitraDividend;

          if (managersOnly.length > 0) {
            const managerPercentage = managersOnly.reduce((sum, m) => sum + (m.percentage || 0), 0);
            const managerDividend = Math.round((managerPercentage / 100) * profitAfterMitra);
            totalManagerDividend += managerDividend;
            totalInvestorDividend += (profitAfterMitra - managerDividend);
          } else {
            totalInvestorDividend += profitAfterMitra;
          }
        });

        const labaDitahanCabang = totalMitraDividend + totalManagerDividend;
        const labaDitahanInvestor = totalInvestorDividend;

        setPreviousPeriodRetainedEarnings({
          periodName: previousPeriodRetainedEarnings.periodName,
          labaDitahanCabang,
          labaDitahanInvestor,
          loading: false
        });
      } catch (error) {
        console.error('Failed to fetch previous period retained earnings:', error);
        setPreviousPeriodRetainedEarnings(null);
      }
    };

    fetchPreviousPeriodRetainedEarnings();
  }, [previousPeriodRetainedEarnings]);

  return {
    previousPeriodRetainedEarnings,
    setPreviousPeriodRetainedEarnings
  };
};
